import {
  Component,
  ChangeDetectionStrategy,
  input,
  OnInit,
  OnDestroy,
  viewChild,
  inject,
  signal,
  computed,
} from '@angular/core';
import { NgStyle } from '@angular/common';
import { ModalController } from '@ionic/angular/standalone';
import { TranslocoService } from '@jsverse/transloco';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { safeModalDismiss } from 'src/app/core/services/modal/modal.utils';
import { EmblaCarouselDirective, EmblaCarouselType, EmblaEventType } from 'embla-carousel-angular';
import { NavButtonComponent } from 'src/app/ui/buttons/nav-button/nav-button.component';
import { PageIndicatorComponent } from 'src/app/ui/feedback/page-indicator/page-indicator.component';
import { ButtonComponent } from 'src/app/ui/buttons/button/button.component';
import { ThemePipe } from 'src/app/core/pipes/theme.pipe';

@Component({
  selector: 'app-tutorial-wizard',
  templateUrl: './tutorial-wizard.component.html',
  styleUrls: ['./tutorial-wizard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgStyle,
    EmblaCarouselDirective,
    NavButtonComponent,
    PageIndicatorComponent,
    ButtonComponent,
    ThemePipe,
  ],
})
export class TutorialWizardComponent implements OnInit, OnDestroy {
  totalPages = input.required<number>();
  slideTitles = input<string[]>([]);
  initialPage = input(0);
  // Last step's label. 'finish' reads "Let's play!", so tutorials that don't lead into a game use 'got-it'.
  finishKey = input<'finish' | 'got-it'>('finish');
  private emblaRef = viewChild(EmblaCarouselDirective);

  private static readonly ClickDebounceMs = 200;

  private modalController = inject(ModalController);
  private themeService = inject(ThemeService);
  private translocoService = inject(TranslocoService);

  private isProcessing = signal(false);
  private lastClickTime = 0;

  currentPage = signal(0);

  // Embla carousel integration
  private emblaApi?: EmblaCarouselType;
  private attemptingClose = false;
  private dragStartedOnLastPage = false;
  private isDragging = false;

  readonly emblaOptions = {
    loop: false,
    align: 'start' as const,
    containScroll: 'trimSnaps' as const,
    dragFree: false,
    skipSnaps: false,
    watchDrag: true, // Enable mouse drag on desktop
    duration: 20, // Animation duration
  };

  readonly subscribeToEvents: EmblaEventType[] = [
    'init',
    'select',
    'pointerDown',
    'settle',
    'scroll',
    'pointerUp',
  ];

  readonly drawerConfig = this.themeService.payDrawerConfig();

  readonly drawerStyles: [string, string][] = [
    ['background', 'drawer'],
    ['border-radius-top', 'drawer'],
  ];

  readonly titleStyles: [string, string][] = [
    ['type', 'drawer-tutorial-title'],
    ['text-color', 'drawer-tutorial-title'],
  ];

  get isLastPage(): boolean {
    return this.currentPage() === this.totalPages() - 1;
  }

  readonly currentPageTitle = computed(() => {
    const page = this.currentPage();
    const titles = this.slideTitles();
    if (titles.length > page) {
      return titles[page];
    }
    return '';
  });

  readonly nextLabel = computed(() => {
    const key = this.isLastPage ? this.finishKey() : 'next';
    return this.translocoService.translate(`components.tutorial-wizard.${key}`);
  });

  ngOnInit(): void {
    this.currentPage.set(this.initialPage());
  }

  ngOnDestroy(): void {
    this.emblaApi?.destroy();
  }

  onEmblaChange(event: EmblaEventType): void {
    switch (event) {
      case 'init': {
        // Initialize API when Embla is ready
        const ref = this.emblaRef();
        if (ref?.emblaApi) {
          this.emblaApi = ref.emblaApi;
          // Set initial slide
          this.emblaApi.scrollTo(this.initialPage(), true); // true = instant, no animation
        }
        break;
      }

      case 'select': {
        if (!this.emblaApi) {
          return;
        }
        // Slide changed - update current page and reset swipe state
        const newIndex = this.emblaApi.selectedScrollSnap();
        if (newIndex !== this.currentPage()) {
          this.currentPage.set(newIndex);
        }
        this.attemptingClose = false;
        this.dragStartedOnLastPage = false;
        this.isDragging = false;
        break;
      }

      case 'pointerDown':
        // Drag started - track if starting on last page
        this.attemptingClose = false;
        this.isDragging = true;
        this.dragStartedOnLastPage = this.currentPage() === this.totalPages() - 1;
        break;

      case 'settle':
        // Drag ended
        this.isDragging = false;
        break;

      case 'scroll': {
        if (!this.emblaApi) {
          return;
        }
        // Track scroll progress for swipe-to-close detection
        const scrollProgress = this.emblaApi.scrollProgress();
        // Only detect swipe-to-close if drag STARTED on last page and trying to scroll beyond
        if (this.dragStartedOnLastPage && scrollProgress >= 1.0 && this.isDragging) {
          this.attemptingClose = true;
        }
        break;
      }

      case 'pointerUp':
        // Pointer released - close if attempting close
        if (this.attemptingClose) {
          this.onCloseClick();
        }
        this.isDragging = false;
        break;
    }
  }

  async onCloseClick(): Promise<void> {
    await safeModalDismiss(this.modalController);
  }

  async onNextClick(): Promise<void> {
    const now = Date.now();
    if (this.isProcessing() || now - this.lastClickTime < TutorialWizardComponent.ClickDebounceMs) {
      return;
    }

    this.lastClickTime = now;
    this.isProcessing.set(true);
    try {
      const nextPage = this.currentPage() + 1;

      // If we'd go beyond the last page, close instead
      if (nextPage > this.totalPages() - 1) {
        await this.onCloseClick();
        return;
      }

      // Otherwise, navigate to the next page
      if (this.emblaApi) {
        this.emblaApi.scrollNext();
      } else {
        // Fallback if API not ready
        this.currentPage.set(nextPage);
      }
    } finally {
      this.isProcessing.set(false);
    }
  }
}
